// PILOTAGE.UNIFIED.5 — helpers PURS de présentation opérateur. Aucune I/O, aucune décision Flight Control.
// Lit uniquement les états déjà exposés (orchestration backend + status Raspberry) et en dérive l'affichage.

export const PIPELINE_STEPS = [
  { key: "prepare", label: "PREPARE" },
  { key: "arm", label: "ARM" },
  { key: "takeoff", label: "TAKEOFF" },
  { key: "loiter", label: "LOITER" },
  { key: "stabilizing", label: "STABILIZING" },
  { key: "prestream", label: "PRESTREAM" },
  { key: "offboard_entering", label: "OFFBOARD ENTERING" },
  { key: "offboard_active", label: "OFFBOARD ACTIVE" },
  { key: "position_hold", label: "POSITION HOLD" },
];

const TERMINAL = ["FAILED", "OFFBOARD_NOT_ACQUIRED", "CANCELLED"];

// phase backend -> clé d'étape « en cours » (executing). Diagnostic uniquement (jamais de timer).
const PHASE_TO_STEP = {
  GROUND_PREPARE: "prepare",
  ARMING: "arm",
  TAKING_OFF: "takeoff",
  WAITING_LOITER: "loiter",
  STABILIZING: "stabilizing",
  PRESTREAM: "prestream",
  OFFBOARD_ENTERING: "offboard_entering",
  OFFBOARD_ACTIVE: "offboard_active",
};

const _diffMs = (a, b) => {
  // Durée UNIQUEMENT à partir de DEUX timestamps backend ISO cohérents. Sinon null (jamais fabriqué).
  if (!a || !b) return null;
  const ta = Date.parse(a), tb = Date.parse(b);
  if (!Number.isFinite(ta) || !Number.isFinite(tb) || tb < ta) return null;
  return tb - ta;
};

// COCKPIT.FINAL.5 — projection pipeline UNIQUE sur PREUVES backend (snapshot.pipeline). PURE, jamais
// de state machine. États : pending | executing | executed | failed | result_unknown | cancelled |
// unknown. Un succès prouvé (executed) est CONSERVÉ même après un terminal ultérieur. L'étape exacte
// d'un échec inconnu reste `unknown` (jamais `failed` arbitraire). Aucun ACK/timestamp fabriqué.
export function buildFlightPipeline({ orchPhase, pipeline, reason } = {}) {
  const p = pipeline && typeof pipeline === "object" ? pipeline : {};
  const terminal = orchPhase && TERMINAL.includes(orchPhase) ? orchPhase : null;
  const currentKey = terminal ? null : PHASE_TO_STEP[orchPhase] || null;

  // Attribution result_unknown depuis le reason backend (prepare_/arm_/takeoff_result_unknown).
  const ru = typeof reason === "string" && reason.endsWith("result_unknown") ? reason.split("_")[0] : null;

  // finishedAt (ISO backend) par étape — uniquement quand réellement exposé.
  const fin = {
    prepare: p.prepare?.executed_at ?? null,
    arm: p.arm?.executed_at ?? null,
    takeoff: p.takeoff?.executed_at ?? null,
    loiter: p.loiter?.confirmed_at ?? null,
    stabilizing: null, prestream: null, offboard_entering: null,
    offboard_active: p.offboard_active?.at ?? null,
    position_hold: p.position_hold?.at ?? null,
  };
  const prevFinish = (key) => {
    const idx = PIPELINE_STEPS.findIndex((s) => s.key === key);
    for (let j = idx - 1; j >= 0; j--) { if (fin[PIPELINE_STEPS[j].key]) return fin[PIPELINE_STEPS[j].key]; }
    return p.started_at ?? null;
  };

  // État « prouvé » (executed / failed / result_unknown) par étape, sinon null (indéterminé).
  const proven = (key) => {
    if (ru === key) return "result_unknown";
    switch (key) {
      case "prepare": return p.prepare?.executed_at ? "executed" : null;
      case "arm": return p.arm?.executed === true ? "executed" : p.arm?.executed === false ? "failed" : null;
      case "takeoff": return p.takeoff?.executed === true ? "executed" : p.takeoff?.executed === false ? "failed" : null;
      case "loiter": return p.loiter?.confirmed === true ? "executed" : null;
      case "stabilizing": return p.stabilizing?.reached === true ? "executed" : null;
      case "prestream": return p.prestream?.reached === true ? "executed" : null;
      case "offboard_entering": return p.offboard_entering?.reached === true ? "executed" : null;
      case "offboard_active": return p.offboard_active?.reached === true ? "executed" : null;
      case "position_hold": return p.position_hold?.reached === true ? "executed" : null;
      default: return null;
    }
  };

  const curIdx = currentKey ? PIPELINE_STEPS.findIndex((s) => s.key === currentKey) : -1;

  const steps = PIPELINE_STEPS.map((s, i) => {
    let state = proven(s.key); // preuve prioritaire — conservée même en terminal
    if (!state) {
      if (terminal === "CANCELLED") state = "cancelled";
      else if (terminal === "OFFBOARD_NOT_ACQUIRED") state = s.key === "offboard_active" ? "failed" : "unknown";
      else if (terminal === "FAILED") state = "unknown"; // étape exacte inconnue -> jamais failed arbitraire
      else if (currentKey && i === curIdx) state = "executing";
      else if (currentKey && i < curIdx) state = "unknown"; // passé sans preuve -> pas fabriqué executed
      else state = "pending";
    }
    const finishedAt = fin[s.key];
    const startedAt = finishedAt ? prevFinish(s.key) : null;
    const commandId = s.key === "prepare" ? (p.prepare?.command_id ?? null) : null;
    const stepReason = s.key === "position_hold" ? (p.position_hold?.reason ?? null)
      : ((state === "failed" || state === "result_unknown") ? (reason ?? null) : null);
    return {
      ...s,
      state,
      startedAt: startedAt ?? null,
      finishedAt: finishedAt ?? null,
      durationMs: _diffMs(startedAt, finishedAt),
      reason: stepReason,
      commandId,
      ack: null, // ACK par étape non exposé par le backend -> jamais reconstruit
    };
  });
  return { terminal, steps };
}

// COCKPIT.FINAL.3 — projection UI UNIQUE de l'autorité de contrôle. PURE (aucune I/O, aucune state
// machine) : simple PROJECTION d'affichage des états déjà exposés (orchestration backend + statut
// raspberry). Répond « Qui contrôle le drone ? » de façon FAIL-CLOSED : information insuffisante ou
// périmée => UNKNOWN. N'invente JAMAIS une autorité saine, ne déduit JAMAIS OFFBOARD depuis la seule
// intention frontend. Priorité SÉCURITÉ : FAILSAFE > PILOT_RC > SKYCORE_OFFBOARD > PX4/GROUND > UNKNOWN,
// mais uniquement quand les preuves requises existent (null n'est JAMAIS traité comme false).
export const CONTROL_AUTHORITY_UI = {
  GROUND_PX4: { label: "GROUND / PX4", tone: "neutral" },
  PX4_MANUAL: { label: "PX4 MANUAL", tone: "neutral" },
  PX4_AUTO: { label: "PX4 AUTO", tone: "neutral" },
  SKYCORE_OFFBOARD: { label: "SKYCORE OFFBOARD", tone: "ok" },
  PILOT_RC: { label: "PILOT RC PRIORITAIRE", tone: "warn" },
  FAILSAFE: { label: "FAILSAFE", tone: "danger" },
  UNKNOWN: { label: "UNKNOWN", tone: "unknown" },
  // D5.31 — aucune connexion de pilotage : l'autorité PX4 n'est pas OBSERVÉE (≠ autorité inconnue).
  NOT_CONNECTED: { label: "NON CONNECTÉ", tone: "unknown" },
};

// Classification DÉTERMINISTE et DOCUMENTÉE des modes PX4 (source : px4_flight_controller
// _PX4_MAIN_NAMES / _PX4_AUTO_SUB_NAMES). MANUAL-family = le pilote vole directement ; AUTO-family =
// l'autopilote vole. OFFBOARD est traité séparément (autorité SkyCore). Tout mode hors de ces
// ensembles => UNKNOWN (jamais de liste approximative, jamais de classification incertaine).
const PX4_MANUAL_MODES = new Set(["MANUAL", "STABILIZED", "ACRO", "ALTCTL", "POSCTL", "RATTITUDE"]);
const PX4_AUTO_MODES = new Set(["AUTO", "LOITER", "MISSION", "RTL", "RTGS", "TAKEOFF", "LAND", "FOLLOW_TARGET", "PRECLAND", "READY"]);

export function deriveControlAuthority(inputs) {
  const { orch, status, telemetryFresh, conn } = inputs || {};
  const ca = orch && typeof orch === "object" ? orch.control_authority : undefined;
  const st = status && typeof status === "object" ? status : null;
  const px4 = st && st.px4_mode ? String(st.px4_mode).toUpperCase() : null;
  const mk = (authority, reason, source, movementAllowed) => ({
    authority,
    label: CONTROL_AUTHORITY_UI[authority].label,
    tone: CONTROL_AUTHORITY_UI[authority].tone,
    reason: reason || null,
    source: source || null,
    movementAllowed: movementAllowed === true,
  });

  // 1. FAILSAFE — sécurité dominante. Preuve EXPLICITE uniquement (null n'est jamais false).
  if (ca === "FAILSAFE" || (st && st.failsafe === true)) {
    return mk("FAILSAFE", "Failsafe actif",
      ca === "FAILSAFE" ? "orchestration backend" : "statut raspberry", false);
  }
  // 2. PILOT_RC — reprise RC pilote, PRIORITAIRE, aucune auto-reprise SkyCore. Mouvement verrouillé.
  if (ca === "PILOT_RC" || (st && (st.state === "PILOT_OVERRIDE" || st.pilot_override === true))) {
    return mk("PILOT_RC", "Reprise RC pilote — contrôle SkyCore suspendu",
      ca === "PILOT_RC" ? "orchestration backend" : "statut raspberry", false);
  }
  // Aucune source autoritaire -> UNKNOWN (fail-closed).
  if (!orch && !st) {
    if (conn !== undefined && conn !== "open" && conn !== "connecting") {
      return mk("NOT_CONNECTED", "Aucune connexion de pilotage — autorité PX4 non observée", null, false);
    }
    return mk("UNKNOWN", "Aucune donnée d'autorité", null, false);
  }

  // 3. SKYCORE_OFFBOARD — UNIQUEMENT si le backend l'a PROUVÉ (source=raspberry + state=ACTIVE +
  //    px4_mode=OFFBOARD) ET télémétrie FRAÎCHE. Jamais déduit de l'intention/phase frontend.
  if (ca === "SKYCORE_OFFBOARD") {
    if (telemetryFresh === true) {
      return mk("SKYCORE_OFFBOARD", "OFFBOARD actif — autorité SkyCore confirmée",
        "orchestration backend", orch.movement_allowed === true);
    }
    return mk("UNKNOWN", "OFFBOARD non confirmé (télémétrie périmée)", "orchestration backend", false);
  }

  // À partir d'ici (modes PX4 / sol) : exiger télémétrie FRAÎCHE + statut raspberry autoritaire.
  if (telemetryFresh !== true) return mk("UNKNOWN", "Télémétrie périmée", "statut raspberry", false);
  if (!st || st.source !== "raspberry") return mk("UNKNOWN", "Statut non autoritaire", null, false);

  // 4. GROUND_PX4 — posé (landed_state ON_GROUND prouvé), aucune autre autorité.
  if (st.landed_state === 1) return mk("GROUND_PX4", "Au sol", "statut raspberry", false);

  // 5. PX4_MANUAL / PX4_AUTO — classification DÉTERMINISTE depuis px4_mode documenté.
  if (px4 && PX4_MANUAL_MODES.has(px4)) return mk("PX4_MANUAL", `Mode PX4 : ${px4}`, "statut raspberry", false);
  if (px4 && PX4_AUTO_MODES.has(px4)) return mk("PX4_AUTO", `Mode PX4 : ${px4}`, "statut raspberry", false);

  // 6. Classification incertaine -> UNKNOWN (jamais d'autorité approximative).
  return mk("UNKNOWN", px4 ? `Mode PX4 non classifiable : ${px4}` : "Mode PX4 inconnu", "statut raspberry", false);
}

// « PRÊT À LANCER » (et NON « prêt à armer ») : la séquence PREPARE→ARM→TAKEOFF peut être demandée.
export function operatorReadiness({ conn, canStartFlight, altitudeValid } = {}) {
  if (conn === "connecting") return { state: "connecting", label: "CONNEXION…" };
  if (conn !== "open") return { state: "disconnected", label: "CONTRÔLE NON CONNECTÉ" };
  if (canStartFlight === true && altitudeValid === true) return { state: "ready", label: "PRÊT À LANCER" };
  return { state: "not_ready", label: "NON PRÊT" };
}

// COCKPIT.FINAL.4 — projection Safety UNIQUE. PURE (aucune I/O, aucune state machine) : simple
// PROJECTION d'affichage « le drone est-il sûr / dégradé / inconnu / en failsafe ? ». FAIL-CLOSED :
// null / undefined / stale ≠ OK. failsafe explicite dominant ; aucun « RC OK » fabriqué ; aucun seuil
// batterie inventé. États item : OK | WARN | FAIL | UNKNOWN. Global : OK | DEGRADED | FAILSAFE | UNKNOWN.
// D5.31 — INFO : mesure fraîche et valide, purement informative (jamais une autorisation, hors global).
export const SAFETY_TONE = { OK: "#22C55E", WARN: "#F59E0B", FAIL: "#EF4444", UNKNOWN: "#A3A3A3", INFO: "#3B82F6" };

export function deriveSafetyView(inputs) {
  const { drone, status, orch, telemetryFresh } = inputs || {};
  const d = drone && typeof drone === "object" ? drone : null;
  const st = status && typeof status === "object" ? status : null;
  const ca = orch && typeof orch === "object" ? orch.control_authority : undefined;
  const fresh = telemetryFresh === true;
  const item = (state, value, reason) => ({ state, value: value == null ? null : value, reason: reason || null });

  // telemetry — fraîcheur live. stale/missing ne sont JAMAIS OK.
  let telemetry;
  if (fresh) telemetry = item("OK", "fraîche");
  else if (d && d.last_seen) telemetry = item("WARN", "périmée", "Dernière télémétrie trop ancienne");
  else telemetry = item("UNKNOWN", null, "Aucune télémétrie");

  // mavlink — true→OK, false→FAIL, null→UNKNOWN ; non fiable si télémétrie non fraîche.
  let mavlink;
  const mav = d ? d.mavlink_connected : undefined;
  if (!fresh) mavlink = item("UNKNOWN", null, "Télémétrie non fraîche");
  else if (mav === true) mavlink = item("OK", "connecté");
  else if (mav === false) mavlink = item("FAIL", "déconnecté", "Lien MAVLink absent");
  else mavlink = item("UNKNOWN", null, "MAVLink inconnu");

  // rc — aucune santé de lien RC propagée. PILOT_OVERRIDE prouve une reprise pilote, PAS la santé du
  // lien : jamais « RC OK ». Override -> WARN (attention) ; sinon UNKNOWN.
  let rc;
  const override = ca === "PILOT_RC" || (st && (st.state === "PILOT_OVERRIDE" || st.pilot_override === true));
  if (override) rc = item("WARN", "PILOT OVERRIDE", "Reprise RC pilote");
  else rc = item("UNKNOWN", null, "Aucun état de lien RC propagé");

  // gps — via gps_state autoritaire (fix/no_fix/unavailable). Aucun seuil satellite inventé ici.
  let gps;
  if (!fresh) gps = item("UNKNOWN", null, "Télémétrie non fraîche");
  else {
    const gstate = d ? d.gps_state : null;
    const sats = d && d.sats != null ? `${d.sats} sats` : null;
    if (gstate === "fix") gps = item("OK", sats || "fix 3D");
    else if (gstate === "no_fix") gps = item("WARN", sats || "pas de fix", "Pas de fix GPS 3D");
    else gps = item("UNKNOWN", sats, "Fix GPS inconnu");
  }

  // position — global_position_valid : true→OK, false→FAIL, null→UNKNOWN.
  let position;
  if (!fresh) position = item("UNKNOWN", null, "Télémétrie non fraîche");
  else {
    const gpv = d ? d.global_position_valid : undefined;
    if (gpv === true) position = item("OK", "valide");
    else if (gpv === false) position = item("FAIL", "invalide", "Position globale invalide");
    else position = item("UNKNOWN", null, "Position inconnue");
  }

  // home — séparé. true→OK, false→WARN (HOME invalide ≠ FAILSAFE global), null→UNKNOWN.
  let home;
  if (!fresh) home = item("UNKNOWN", null, "Télémétrie non fraîche");
  else {
    const hpv = d ? d.home_position_valid : undefined;
    const age = d && typeof d.home_position_age_s === "number" ? `${Math.round(d.home_position_age_s)} s` : null;
    if (hpv === true) home = item("OK", age || "valide");
    else if (hpv === false) home = item("WARN", "invalide", "HOME non défini/invalide");
    else home = item("UNKNOWN", null, "HOME inconnu");
  }

  // battery — AUCUN contrat sécurité continu défini dans SKYCORE (seul un gate 15 % au lancement) :
  // purement INFORMATIF ici -> on affiche la valeur mais l'état reste UNKNOWN (aucun seuil inventé).
  let battery;
  const bat = d && typeof d.battery === "number" ? d.battery : null;
  // D5.31 — mesure fraîche ET battery_valid === true ET non périmée => INFO « mesure valide » (aucun seuil,
  // aucune autorisation de vol ; reste hors du calcul global). Sinon UNKNOWN (absence ≠ valide).
  if (bat != null && fresh && d.battery_valid === true && d.battery_stale !== true) {
    battery = item("INFO", `${Math.round(bat)} % · mesure valide`, "Mesure fraîche — information, pas une autorisation de vol");
  } else if (bat != null) {
    battery = item("UNKNOWN", `${Math.round(bat)} %`, !fresh ? "Télémétrie non fraîche" : d.battery_valid === false ? "Mesure batterie invalide" : "Validité de la mesure non confirmée");
  } else battery = item("UNKNOWN", null, "Batterie inconnue");

  // px4Failsafe — drapeau autoritaire. true→FAIL, false→OK, null→UNKNOWN (null n'est jamais false).
  let px4Failsafe;
  const fs = st ? st.failsafe : undefined;
  if (fs === true) px4Failsafe = item("FAIL", "TRUE", "PX4 failsafe actif");
  else if (fs === false) px4Failsafe = item("OK", "FALSE");
  else px4Failsafe = item("UNKNOWN", null, st ? "Failsafe non rapporté par le Raspberry" : "Statut PX4 non reçu (aucune connexion de pilotage)");

  const items = { telemetry, mavlink, rc, gps, position, home, battery, px4Failsafe };

  // GLOBAL — priorité : FAILSAFE explicite > FAIL/WARN opérationnel (DEGRADED) > core UNKNOWN > OK.
  // Les indicateurs purement informatifs (rc, battery) ne font JAMAIS échouer le global.
  const failsafeActive = fs === true || ca === "FAILSAFE";
  let global, primaryAlert = null, reason = null;
  if (failsafeActive) {
    global = "FAILSAFE";
    primaryAlert = "FAILSAFE ACTIF";
    reason = fs === true ? "PX4 failsafe actif" : "Autorité FAILSAFE (orchestration)";
  } else {
    const opItems = [["MAVLink", mavlink], ["Position", position], ["GPS", gps], ["HOME", home]];
    const fail = opItems.find(([, it]) => it.state === "FAIL");
    const warn = opItems.find(([, it]) => it.state === "WARN");
    const core = [telemetry, mavlink, position, px4Failsafe];
    if (fail) { global = "DEGRADED"; primaryAlert = `${fail[0]} : ${fail[1].reason || "défaut"}`; reason = fail[1].reason; }
    else if (warn) { global = "DEGRADED"; primaryAlert = `${warn[0]} : ${warn[1].reason || "dégradé"}`; reason = warn[1].reason; }
    else if (core.some((it) => it.state === "UNKNOWN")) {
      const missing = [["Télémétrie", telemetry], ["MAVLink", mavlink], ["Position", position], ["PX4 Failsafe", px4Failsafe]]
        .filter(([, it]) => it.state === "UNKNOWN").map(([n]) => n);
      global = "UNKNOWN"; primaryAlert = "État sécurité incomplet"; reason = `Informations sécurité insuffisantes : ${missing.join(", ")}`;
    }
    else { global = "OK"; }
  }
  return { global, items, primaryAlert, reason };
}


// Vue station keeping, corrélée à la session courante (évite de présenter d'anciennes métriques comme live).
export function stationKeepingView({ sessionId, status } = {}) {  const live = sessionId != null && status?.session_id != null && status.session_id === sessionId;
  if (!live) return { live: false };
  const sk = status?.station_keeping || {};
  const mx = status?.station_keeping_metrics || {};
  const active = sk.active === true && sk.setpoint_mode === "POSITION_HOLD";
  return {
    live: true,
    active,
    mode: sk.setpoint_mode ?? null,
    anchorReason: sk.anchor_reason ?? null,
    anchorAge: sk.anchor_age_s ?? null,
    anchor: mx.anchor ?? sk.anchor ?? null,
    errXY: mx.current_hold_error_xy_m ?? null,
    errZ: mx.current_hold_error_z_m ?? null,
    maxXY: mx.max_hold_error_xy_m ?? null,
    maxZ: mx.max_hold_error_z_m ?? null,
    rateHz: mx.position_hold_rate_hz ?? null,
    gapMs: mx.position_hold_max_gap_ms ?? null,
    jitterMs: mx.position_hold_jitter_ms ?? null,
    segments: mx.segment_count ?? null,
  };
}

// UNIFIED.6 — HOME PX4 pur. Cartographiable UNIQUEMENT si source Raspberry autoritaire + valid === true
// + lat/lon FINIS et dans les bornes. 0 est une coordonnée valide (jamais de test de truthiness).
// Altitude AMSL et âge peuvent rester null (UNKNOWN jamais fabriqué).
export function homePositionView(status) {
  const hp = status?.home_position;
  const finite = (v) => typeof v === "number" && Number.isFinite(v);
  const none = { mappable: false, valid: false, lat: null, lon: null, altAmsl: null, ageS: null };
  if (status?.source !== "raspberry" || !hp || hp.valid !== true) return none;
  const lat = hp.lat;
  const lon = hp.lon;
  if (!finite(lat) || !finite(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) return none;
  return {
    mappable: true,
    valid: true,
    lat,
    lon,
    altAmsl: finite(hp.alt_amsl) ? hp.alt_amsl : null,
    ageS: finite(hp.age_s) ? hp.age_s : null,
  };
}
